/**
 * Il Cruscotto aperto si accorge che ne è uscito uno nuovo.
 *
 * ## Il fatto
 *
 * Il 2026-10-21 la cura del costruttore viaggi ([[K714]]) è andata in servizio alle 09:40, e
 * alle 09:54, 09:57 e 10:07 Sabrina Brabanti ha ripreso **lo stesso rifiuto** di prima: la sua
 * scheda era aperta dalle 09:02, e il file del costruttore che aveva in memoria era quello
 * vecchio (`CostruttoreViaggiView-C6y276V9.js`, misurato nel registro di nginx; il nuovo non
 * l'ha mai chiesto). Una pagina che non si ricarica gira il codice del giorno in cui è stata
 * aperta: ogni rilascio arriva a chi apre il Cruscotto **dopo**, e a chi lo tiene aperto tutto il
 * giorno — chi pianifica, la reception — mai. E c'è di peggio: il rilascio toglie i file vecchi,
 * quindi una pagina mai visitata in quella scheda non si carica affatto.
 *
 * ## Come
 *
 * La compilazione scrive `versione.json` con un'impronta sua (`vite.config.ts`) e la stessa
 * impronta dentro il codice (`__BUILD_ID__`). Qui si rilegge il file ogni cinque minuti e
 * quando la scheda torna davanti: se l'impronta è diversa, è uscito un Cruscotto nuovo.
 *
 * Che cosa se ne fa lo decide chi guarda, in due tempi:
 * - **al primo cambio di pagina** si carica il nuovo (`router.beforeEach`): cambiare pagina
 *   butta via la pagina di prima comunque, quindi non si perde niente che non si perdesse già;
 * - **mentre si lavora** non si ricarica niente da sé — un viaggio a metà, un modulo scritto —
 *   ma `AvvisoVersioneNuova` lo dice, con «Aggiorna adesso» per chi vuole.
 */
import { ref } from 'vue';

/** Vera quando sul server c'è un Cruscotto diverso da quello che gira in questa scheda. */
export const versioneNuova = ref(false);

const OGNI = 5 * 60 * 1000;

export async function controllaVersione(): Promise<void> {
  if (versioneNuova.value) return;
  try {
    const r = await fetch('/versione.json', { cache: 'no-store' });
    if (!r.ok) return;
    const { build } = (await r.json()) as { build?: unknown };
    // Un file che non dice niente non è una versione nuova: in sviluppo non c'è, e un 404
    // servito come `index.html` non ha `build`.
    if (typeof build === 'string' && build !== __BUILD_ID__) versioneNuova.value = true;
  } catch {
    // La rete che cade non è una versione nuova: si riprova al giro dopo.
  }
}

/**
 * Un file del Cruscotto che non si carica più — il rilascio ha tolto quelli vecchi — si cura
 * ricaricando. Una volta sola ogni minuto: se il nuovo non si carica nemmeno lui, ricaricare
 * all'infinito non cura niente, e la pagina rotta almeno si vede.
 */
function ricaricaPerFileSparito(evento: Event): void {
  const CHIAVE = 'bestehr.ricaricato-per-file-sparito';
  let ultimo: number;
  try {
    ultimo = Number(sessionStorage.getItem(CHIAVE) ?? 0);
  } catch {
    // Senza sessionStorage non si sa se si è già ricaricato: meglio non ricaricare.
    return;
  }
  if (Date.now() - ultimo < 60_000) return;
  try {
    sessionStorage.setItem(CHIAVE, String(Date.now()));
  } catch {
    return;
  }
  evento.preventDefault();
  window.location.reload();
}

/**
 * Le pagine da chiosco — il widget e la console di Beste Sale — non cambiano mai pagina: la
 * finestra dell'app sta aperta per settimane, e il ricaricamento «al primo cambio di pagina» qui
 * sopra per loro non arriva mai (paginaCheNonCambiaMai). Le chiamano loro, quando non c'è niente a
 * metà. Una volta l'ora al massimo: se dopo il ricaricamento il server desse ancora la pagina
 * vecchia, ricaricare a raffica non curerebbe niente.
 */
let ricaricaRimandata: number | null = null;

export function ricaricaPerVersioneNuova(libera: () => boolean = () => true): void {
  const CHIAVE = 'bestehr.ricaricato-per-versione-nuova';
  try {
    const manca = 60 * 60_000 - (Date.now() - Number(sessionStorage.getItem(CHIAVE) ?? 0));
    if (manca > 0) {
      // Due rilasci nella stessa ora (rimandataAllaFineDellOra): il secondo arrivava qui, si
      // fermava, e non tornava più — `versioneNuova` resta vera, il controllo non rilegge, e chi
      // chiama guarda solo il passaggio da «no» a «sì». Una console di sala che nessuno tocca
      // restava col codice vecchio fino al riavvio del PC. Si riprova allo scadere dell'ora.
      if (ricaricaRimandata === null) {
        ricaricaRimandata = window.setTimeout(() => {
          ricaricaRimandata = null;
          if (libera()) ricaricaPerVersioneNuova(libera);
        }, manca + 1000);
      }
      return;
    }
    sessionStorage.setItem(CHIAVE, String(Date.now()));
  } catch {
    return;
  }
  window.location.reload();
}

export function avviaControlloVersione(): void {
  window.addEventListener('vite:preloadError', ricaricaPerFileSparito);
  void controllaVersione();
  window.setInterval(() => void controllaVersione(), OGNI);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void controllaVersione();
  });
}
